import React from 'react';
import {View, Platform} from 'react-native';
import {createPortal} from 'react-dom';
import BoxChampSetAsideNotice from './BoxChampSetAsideNotice';

/**
 * The set-aside notice, lifted above every pop-up that would otherwise bury it.
 *
 * Why this exists: scanpack_item.js mirrors a hand-off rejection into
 * boxChampSetAsideMessage precisely BECAUSE a pop-up is on screen hiding the hand-off
 * card. But the in-flow notice lives inside a `<View style={{flex: 1}}>`, and
 * react-native-web gives every View a base `position: relative; zIndex: 0`
 * (View/index.js:141-143). That wrapper is therefore its own stacking context and traps
 * the whole banner block inside it — measured on the rendered tree, the in-flow notice
 * ranks at 0 against pop-up overlays at 8, 10, 15 and 20 in the shared parent context.
 * The words were being painted underneath the very pop-up they exist to survive, so the
 * packer got a buzzer and nothing else.
 *
 * Portalled to document.body, following BoxChampReopenPrompt.js:78-80 — the same
 * pattern, in the same feature area, with the same native-safe fallback. The portal is
 * what lets it also clear a react-native <Modal> (boxChampDimsModalOpen), which is
 * itself portalled to document.body and cannot be beaten from inside the app tree.
 *
 * Deliberately NOT a react-native <Modal>, even though BoxChampDimsForm.js:236 uses one:
 * react-native-web's Modal always wraps children in ModalFocusTrap and pushes itself onto
 * activeModalStack (Modal/index.js:96-104), so it would pull focus out of the pop-up the
 * packer is currently typing into — the exact harm boxChampModalOpen() exists to prevent.
 * This renders no focus trap, registers no modal, and takes no focus.
 *
 * pointerEvents="box-none" so the strip never swallows a click meant for the pop-up
 * underneath. Only the notice itself is interactive.
 */
export default function BoxChampEscalatedNotice({
  message,
  tone = 'warning',
  onDismiss,
}) {
  if (!message) return null;

  const layer = (
    <View
      testID="boxchamp-set-aside-escalation-layer"
      pointerEvents="box-none"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10000,
      }}>
      <BoxChampSetAsideNotice
        testID="boxchamp-set-aside-notice-escalated"
        textTestID="boxchamp-set-aside-text-escalated"
        message={message}
        tone={tone}
        onDismiss={onDismiss}
      />
    </View>
  );

  return Platform.OS === 'web' && typeof document !== 'undefined'
    ? createPortal(layer, document.body)
    : layer;
}
